import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { PublicVacancyResponse } from 'clients/CampaignsClient/CampaignsClient.types';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { VacancyDetails } from 'components/ui/organisms/VacancyDetails/VacancyDetails';
import { routes } from 'config/routes';
import { useGTM } from 'hooks/gtm';
import { ConfirmationStatus } from '../RJCommHubCandidateConfirmation/RJCommHubCandidateConfirmation.types';
import { useStyles } from './RJCommHubPublicVacancyDetails.styles';

export const RJCommHubPublicVacancyDetails = () => {
  const [vacancy, setVacancy] = useState<PublicVacancyResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);
  const lastFetchedTokenRef = useRef<string | null>(null);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.replace(/\/+$/, '') || undefined;
  const subscribedParam = searchParams.get('subscribed');
  const subscribed = subscribedParam === null ? undefined : subscribedParam === 'true';
  const navigate = useNavigate();
  const classes = useStyles();
  const gtm = useGTM();

  useEffect(() => {
    if (!token || token.trim() === '') return;

    if (lastFetchedTokenRef.current === token) return;

    const fetchVacancy = async () => {
      if (fetchingRef.current) return;

      fetchingRef.current = true;
      lastFetchedTokenRef.current = token;
      setIsLoading(true);
      setError(null);

      try {
        const { data } = await campaignsClient.getPublicVacancy({ token });

        if (!data || typeof data !== 'object') {
          throw new Error('Invalid response format from API');
        }

        gtm.jobPreviewClickedByCandidate({ jobImprovedId: data.job.db_id });
        setVacancy(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch vacancy');
      } finally {
        setIsLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchVacancy();

    return () => {
      fetchingRef.current = false;
    };
  }, [token]);

  const handleUnsubscribe = () => {
    if (!token) return;
    navigate(`${routes.jobRecommendationsPaused}?token=${encodeURIComponent(token)}`);
  };

  const handleSubmitResponse = async (responseType: ConfirmationStatus) => {
    if (!vacancy) return;

    setIsLoading(true);
    try {
      await campaignsClient.submitCandidateInterest({
        token: responseType === ConfirmationStatus.Positive ? vacancy.accept_token : vacancy.reject_token,
      });

      if (subscribed === undefined) {
        if (responseType === ConfirmationStatus.Positive) {
          navigate(`${routes.rjCommHubCandidateInterested}?token=${token}`);
        } else if (responseType === ConfirmationStatus.Negative) {
          navigate(`${routes.rjCommHubCandidateNotInterested}?token=${token}`);
        }
      } else {
        navigate(`${routes.rjCommHubCandidateConfirmation(responseType)}?token=${token}`);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to submit response');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenQuestionnaire = (questionnaireUrl: string) => {
    window.location.assign(questionnaireUrl);
  };

  return (
    <div css={classes.root}>
      <VacancyDetails
        vacancy={vacancy}
        isLoading={isLoading}
        error={error}
        onSubmit={handleSubmitResponse}
        onUnsubscribe={token ? handleUnsubscribe : undefined}
        candidateAction={vacancy?.candidate_action ?? 'interest_response'}
        questionnaireUrl={vacancy?.questionnaire_url}
        onQuestionnaireClick={handleOpenQuestionnaire}
      />
    </div>
  );
};
